import { useState, useEffect, useCallback } from "react";
import { apiV2Service, organizationApiService, v2PublicApiService } from "../../../../services/apiV2Service";
import * as localService from "../../../../services/localService";
import logger from "../../../../utils/logger";
import { useCountries } from "../../../../hooks/useCountries";

export const useHostClientsData = () => {
  // Use countries hook for caching
  const { countries, countriesLoading: isLoadingCountries } = useCountries();

  // Filter states
  const [industryFilter, setIndustryFilter] = useState("");
  const [industryTypeId, setIndustryTypeId] = useState(null);
  const [countryFilter, setCountryFilter] = useState("");
  const [countryId, setCountryId] = useState(null);
  const [spaceTypeFilter, setSpaceTypeFilter] = useState("");
  const [orderByFilter, setOrderByFilter] = useState("name:asc");
  const [openCompanyAccordion, setOpenCompanyAccordion] = useState(null);
  const [companyManagementOpen, setCompanyManagementOpen] = useState(true);
  const [userManagementOpen, setUserManagementOpen] = useState(true);

  // API data states
  const [organizations, setOrganizations] = useState([]);
  const [organizationsByStatus, setOrganizationsByStatus] = useState({
    ACTIVE: [],
    INACTIVE: [],
    DISABLED: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [currentStatus, setCurrentStatus] = useState("ACTIVE");

  // Organization types and roles states
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [organizationRoles, setOrganizationRoles] = useState([]);

  // Countries are now provided by useCountries hook

  // Fetch all organizations and group by status
  const fetchOrganizations = useCallback(
    async (
      page = 1,
      limit = 50, // Increased limit to get more organizations
      typeId = industryTypeId,
      countryFilterId = countryId,
      spaceType = spaceTypeFilter,
      orderBy = orderByFilter
    ) => {
      setLoading(true);
      setError(null);

      try {
        // Get current user to determine the parent organization
        const currentUser = localService.getUser();
        const parentOrganizationId = currentUser?.organizationId || currentUser?.organization?.id;
        const currentUserOrgTypeCode = currentUser?.organization?.type?.code;

        // Only HOST_PROPERTY_MANAGEMENT can fetch organizations
        if (currentUserOrgTypeCode !== "HOST_PROPERTY_MANAGEMENT") {
          logger.log("User is not HOST_PROPERTY_MANAGEMENT, skipping organization fetch");
          setOrganizations([]);
          setOrganizationsByStatus({ ACTIVE: [], INACTIVE: [], DISABLED: [] });
          setPagination({ totalCount: 0, totalPages: 0, page: 1, limit: 10 });
          setLoading(false);
          return;
        }

        const params = {
          parentId: parentOrganizationId, // Only get child organizations
          // Remove status filter to get all organizations
          page: page,
          limit: limit,
          orderBy: orderBy,
        };

        // Add typeId filter if selected
        if (typeId) {
          params.typeId = typeId;
        }

        // Add countryId filter if selected
        if (countryFilterId) {
          params.countryId = countryFilterId;
        }

        // Add spaceType filter if selected
        if (spaceType) {
          params.spaceType = spaceType;
        }

        const response = await organizationApiService.getOrganizations(params);

        const { data, pagination: pag } = response.data;
        const allOrganizations = Array.isArray(data) ? data : [];

        // Group organizations by status
        const grouped = {
          ACTIVE: [],
          INACTIVE: [],
          DISABLED: [],
        };

        allOrganizations.forEach(org => {
          const status = org.status || "ACTIVE";
          if (grouped[status]) {
            grouped[status].push(org);
          } else {
            // If status is not one of our main categories, put it in ACTIVE as fallback
            grouped.ACTIVE.push(org);
          }
        });

        setOrganizations(allOrganizations);
        setOrganizationsByStatus(grouped);
        setPagination(
          pag && typeof pag.limit === "number" ? pag : { totalCount: 0, totalPages: 0, page: 1, limit: 10 }
        );
        logger.log("Organizations fetched and grouped successfully:", grouped);
      } catch (error) {
        logger.error("Error fetching organizations:", error);
        setError("Failed to load organizations");
        setOrganizations([]);
        setOrganizationsByStatus({ ACTIVE: [], INACTIVE: [], DISABLED: [] });
        setPagination({ totalCount: 0, totalPages: 0, page: 1, limit: 10 });
      } finally {
        setLoading(false);
      }
    },
    [industryTypeId, countryId, spaceTypeFilter, orderByFilter]
  );

  // Fetch organization types
  const fetchOrganizationTypes = useCallback(async () => {
    try {
      const response = await v2PublicApiService.getOrganizationTypes();
      const orgTypesData = response?.data?.data || [];
      setOrganizationTypes(orgTypesData);
      logger.log("Organization types fetched successfully:", orgTypesData);
    } catch (error) {
      logger.error("Error fetching organization types:", error);
      setOrganizationTypes([]);
    }
  }, []);

  // Fetch organization roles
  const fetchOrganizationRoles = useCallback(async () => {
    try {
      const response = await apiV2Service.getOrganizationRoles({
        active: true,
        orderBy: "name:asc",
      });

      // Get current user's organization type ID
      const currentUser = localService.getUser();
      const userOrgTypeId = currentUser?.organization?.typeId;

      // Filter roles by matching organizationTypeId
      const allRoles = response.data || [];
      const filteredRoles = allRoles.filter(role => {
        if (!userOrgTypeId) return true;
        // Include roles that match user's org type
        if (role.organizationTypeId === userOrgTypeId) return true;
        // Also include Individual Customer roles (7) for Property Management hosts (3)
        if (userOrgTypeId === 3 && role.organizationTypeId === 7) return true;
        return false;
      });

      setOrganizationRoles(filteredRoles);
      logger.log("Organization roles fetched and filtered:", {
        userOrgTypeId,
        total: allRoles.length,
        filtered: filteredRoles.length,
      });
    } catch (error) {
      logger.error("Error fetching organization roles:", error);
      setOrganizationRoles([]);
    }
  }, []);

  // Countries are now loaded by useCountries hook

  // Handle status filter change
  const handleStatusChange = statusLabel => {
    const statusMap = {
      Active: "ACTIVE",
      Disabled: "DISABLED",
      Inactive: "INACTIVE",
      Suspended: "SUSPENDED",
    };
    const status = statusMap[statusLabel] || "ACTIVE";
    setCurrentStatus(status);
    fetchOrganizations(status, 1, pagination.limit, industryTypeId, countryId, spaceTypeFilter, orderByFilter);
  };

  // Handle industry filter change
  const handleIndustryFilterChange = industryName => {
    setIndustryFilter(industryName);

    if (!industryName) {
      // Clear filter
      setIndustryTypeId(null);
      fetchOrganizations(currentStatus, 1, pagination.limit, null, countryId, spaceTypeFilter, orderByFilter);
    } else {
      // Find the typeId for the selected industry name
      const selectedType = organizations.find(org => org.type?.name === industryName)?.type;
      if (selectedType) {
        setIndustryTypeId(selectedType.id);
        fetchOrganizations(
          currentStatus,
          1,
          pagination.limit,
          selectedType.id,
          countryId,
          spaceTypeFilter,
          orderByFilter
        );
      }
    }
  };

  // Handle country filter change
  const handleCountryFilterChange = countryName => {
    setCountryFilter(countryName);

    if (!countryName) {
      // Clear filter
      setCountryId(null);
      fetchOrganizations(currentStatus, 1, pagination.limit, industryTypeId, null, spaceTypeFilter, orderByFilter);
    } else {
      // Find the countryId for the selected country name
      const selectedCountry = organizations.find(org => org.country?.name === countryName)?.country;
      if (selectedCountry) {
        setCountryId(selectedCountry.id);
        fetchOrganizations(
          currentStatus,
          1,
          pagination.limit,
          industryTypeId,
          selectedCountry.id,
          spaceTypeFilter,
          orderByFilter
        );
      }
    }
  };

  // Handle space type filter change
  const handleSpaceTypeFilterChange = spaceTypeDisplay => {
    // Convert display value to API value
    const spaceTypeMap = {
      Low: "LOW",
      Medium: "MEDIUM",
      High: "HIGH",
    };
    const spaceTypeApiValue = spaceTypeMap[spaceTypeDisplay] || spaceTypeDisplay.toUpperCase();

    setSpaceTypeFilter(spaceTypeApiValue);
    fetchOrganizations(currentStatus, 1, pagination.limit, industryTypeId, countryId, spaceTypeApiValue, orderByFilter);
  };

  // Handle order by filter change
  const handleOrderByFilterChange = orderByLabel => {
    const orderByMap = {
      "Name: Ascending": "name:asc",
      "Name: Descending": "name:desc",
      "Date: Ascending": "createdAt:asc",
      "Date: Descending": "createdAt:desc",
    };
    const orderBy = orderByMap[orderByLabel] || "name:asc";
    setOrderByFilter(orderBy);
    fetchOrganizations(currentStatus, 1, pagination.limit, industryTypeId, countryId, spaceTypeFilter, orderBy);
  };

  // Handle organization pagination
  const handleOrganizationPageChange = page => {
    fetchOrganizations(
      currentStatus,
      page,
      pagination.limit,
      industryTypeId,
      countryId,
      spaceTypeFilter,
      orderByFilter
    );
  };

  // Data fetching is now handled by the consuming component (index.jsx) based on permissions
  // This hook is now passive and only provides the fetch functions and data state

  return {
    // Filter states
    industryFilter,
    industryTypeId,
    countryFilter,
    countryId,
    spaceTypeFilter,
    orderByFilter,
    openCompanyAccordion,
    setOpenCompanyAccordion,
    companyManagementOpen,
    setCompanyManagementOpen,
    userManagementOpen,
    setUserManagementOpen,

    // API data states
    organizations,
    organizationsByStatus,
    loading,
    error,
    pagination,
    currentStatus,
    organizationTypes,
    organizationRoles,
    countries,
    isLoadingCountries,

    // Actions
    fetchOrganizations,
    fetchOrganizationTypes,
    fetchOrganizationRoles,
    handleStatusChange,
    handleIndustryFilterChange,
    handleCountryFilterChange,
    handleSpaceTypeFilterChange,
    handleOrderByFilterChange,
    handleOrganizationPageChange,
  };
};
